import {
  Button,
  DatePicker,
  ErrorIcon,
  InfoIcon,
  Select as SingleSelect,
  Tooltip,
} from '@lokalise/louis'
import { listTeamLanguagesKeys, useListTeamLanguagesQuery } from '@lokalise/shared'
import type { AiConfigLanguage, Integrations } from '@lokalise/translation-api-schemas'
import clsx from 'clsx'
import { useState } from 'react'
import { requiredErrorMessage } from '../../../../constants/errors'
import { AI_PROFILES_DOCUMENTATION_URL } from '../../../../constants/url'
import {
  appHttpClient,
  authenticatedPublicApiHeaderBuilder,
  getTeamIdFromSession,
} from '../../../../utils/api'
import { TmTargetLanguagesView } from '../../../KeysForLanguage/components/TmTargetLanguagesView/TmTargetLanguagesView'
import {
  KeysForLanguageBorderWrapper,
  KeysForLanguageContainer,
  KeysForLanguageContents,
  KeysForLanguageHeader,
  KeysForLanguagesProjectDetails,
  KeysForLanguagesSourceArrow,
  KeysForLanguagesSourceCard,
  KeysForLanguagesTargetLanguagesContainer,
} from '../../../KeysForLanguage/KeysForLanguage'
import { Select as MultiSelect } from '../../../Select/Select'
import {
  StepContainer,
  StepContents,
  StepRSP,
  StepSubtitle,
  StepTitle,
  StepTitleContainer,
} from '../../../Step/Step'
import { StyledLink } from '../../../StyledLink/StyledLink'
import styles from './TMDataSourceStep.module.css'

type TMDataSourceStepProps = {
  profileName: string
  integration: Integrations
  onNext: () => void
  onClose: () => void
  sourceLang: AiConfigLanguage | undefined
  targetLangs: AiConfigLanguage[]
  date: Date | undefined
  isEdit: boolean
  onChangeSourceLang: (langs: AiConfigLanguage | undefined) => void
  onChangeTargetLangs: (langs: AiConfigLanguage[]) => void
  onChangeDate: (date: Date | undefined) => void
}

export const TMDataSourceStep = ({
  profileName,
  integration,
  sourceLang,
  targetLangs,
  date,
  isEdit,
  onChangeSourceLang,
  onChangeTargetLangs,
  onChangeDate,
  onClose,
  onNext,
}: TMDataSourceStepProps) => {
  const [hasClickedNext, setHasClickedNext] = useState(false)
  const [showSourceChangeWarning, setShowSourceChangeWarning] = useState(false)

  const teamId = getTeamIdFromSession()

  const sourceLangsQueryKey = listTeamLanguagesKeys.teamLanguagesKey({
    teamId,
  })
  const targetLangsQueryKey = listTeamLanguagesKeys.teamLanguagesKey({
    teamId,
    base_language_id: sourceLang?.id,
  })

  const { data: sourceLangsOptions, isPending: isLoadingSourceLangs } = useListTeamLanguagesQuery(
    appHttpClient,
    authenticatedPublicApiHeaderBuilder,
    { queryKey: sourceLangsQueryKey },
  )

  const { data: targetLangsOptions, isPending: isLoadingTargetLangs } = useListTeamLanguagesQuery(
    appHttpClient,
    authenticatedPublicApiHeaderBuilder,
    {
      queryKey: targetLangsQueryKey,
      enabled: !!sourceLang,
    },
  )

  const onClickNext = () => {
    setHasClickedNext(true)

    if (sourceLang && targetLangs.length && onNext) {
      onNext()
    }
  }

  return (
    <StepContainer>
      <StepContents>
        <StepTitleContainer>
          <StepTitle>{isEdit ? 'Edit the data source' : 'Specify the data source'}</StepTitle>
          <StepSubtitle>
            Prioritize languages with high-quality translation memories (TM) to improve translation
            quality. Optionally set TM start date. New translations saved to TM will be reused in
            this profile.{' '}
            <StyledLink href={AI_PROFILES_DOCUMENTATION_URL} rel="noreferrer" target="_blank">
              Docs.
            </StyledLink>
          </StepSubtitle>
        </StepTitleContainer>
        <div className="flex-col gap-2">
          <span id="source-lang-label" className={styles.controlLabel}>
            Choose the source language
          </span>
          <SingleSelect
            aria-labelledby="source-lang-label"
            name="source-langs"
            placeholder="Select..."
            options={sourceLangsOptions?.data.base_languages ?? []}
            value={sourceLang}
            onChange={(lang) => {
              if (lang === null || lang.id === sourceLang?.id) {
                return
              }

              setShowSourceChangeWarning(isEdit && lang?.id !== sourceLang?.id)
              onChangeSourceLang(lang)
              // Target language options depend on selected source language
              // so reset selection when source language changes
              onChangeTargetLangs([])
            }}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => `${option.id}`}
            isLoading={isLoadingSourceLangs}
            error={hasClickedNext && !sourceLang && requiredErrorMessage}
          />
          {showSourceChangeWarning && (
            <div className={styles.warning}>
              <ErrorIcon size="16px" />
              <span>
                Using a source language that differs from the project’s original language can
                negatively affect the quality of translations
              </span>
            </div>
          )}
        </div>
        <MultiSelect
          title="Choose the target languages"
          name="target-langs"
          placeholder="Select..."
          options={targetLangsOptions?.data.target_languages ?? []}
          selectedOptions={targetLangs}
          onSelectionChange={(langs) => onChangeTargetLangs([...langs])}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => `${option.id}`}
          isLoading={isLoadingTargetLangs || isLoadingSourceLangs}
          isDisabled={!sourceLang}
          error={hasClickedNext && !targetLangs.length ? requiredErrorMessage : undefined}
          maxVisibleOptions={5}
        />
        <div className="flex-col gap-2">
          <div className="flex gap-1">
            <span className={styles.controlLabel} id="created-after-label">
              Use only TM entries created after date (optional)
            </span>
            <Tooltip
              classNames={{ content: styles.tooltip }}
              placement="bottom"
              tooltip="Set the starting date for using translation memory (TM) entries. Only TM entries created or updated after this date will be used as context for translations. Leave this field empty if you want to use your entire TM without date restrictions."
            >
              <span className="flex items-center">
                <InfoIcon size="16px" />
              </span>
            </Tooltip>
          </div>
          <DatePicker
            aria-labelledby="created-after-label"
            className={styles.dateControl}
            inPortal
            onChange={onChangeDate}
            onClear={() => onChangeDate(undefined)}
            value={date}
          />
        </div>
        <div className={clsx([styles.footer, 'flex items-end justify-end gap-3'])}>
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onClickNext}>
            Next
          </Button>
        </div>
      </StepContents>
      <StepRSP>
        <KeysForLanguageContainer>
          <KeysForLanguageBorderWrapper>
            <KeysForLanguageHeader name={profileName} integration={integration} />
            <KeysForLanguageContents>
              <KeysForLanguagesSourceCard highlighted sourceLanguage={sourceLang} />

              <KeysForLanguagesSourceArrow />

              <KeysForLanguagesTargetLanguagesContainer>
                <TmTargetLanguagesView
                  highlighted
                  targetLangs={targetLangs}
                  afterDate={date?.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: '2-digit',
                  })}
                />
              </KeysForLanguagesTargetLanguagesContainer>

              <KeysForLanguagesProjectDetails />
            </KeysForLanguageContents>
          </KeysForLanguageBorderWrapper>
        </KeysForLanguageContainer>
      </StepRSP>
    </StepContainer>
  )
}
